import React, {
  Dispatch,
  LegacyRef,
  RefObject,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import ReactQuill from 'react-quill-new';
import styled from 'styled-components';
import 'react-quill/dist/quill.snow.css';

import Column from 'components/common/Column';
import Row from 'components/common/Row';
import { ModifyValue } from 'containers/ProductDetail/hooks/useProductDetaillHooks';
import ModalContainer from 'containers/ProductRegist/components/common/ModalContainer';
import { useIntroduceModifyHooks } from './hooks/useIntroductModifyHooks';
import SaveButton from '../SaveButton';
import { Product, ProductState } from 'types/api';
import HackleManager from 'utils/hackle';
import ModalHeader from './ModalHeader';
import TitleContainer from 'containers/ProductRegist/components/TitleContainer';
import EditorFormat, {
  formats,
  toolbarContainer,
} from 'components/Editor/EditorFormat';
import ImageUploadLoading from 'components/Loading/ImageUploadLoading';
import {
  callWebviewHandler,
  isWebview,
  WebviewOperatorType,
} from 'utils/webview';
import QuillTableBetter from 'quill-table-better';
import {
  handleTransformYouTubeVideoUrlByClick,
  handleTransformYouTubeVideoUrlByEnter,
  initEditorValue,
} from 'utils/editor';
import { normalizeTableTemporary } from 'utils/editorContent';
import GoTopButton from 'components/GoTopButton/goTopButton';
import { debounce } from 'lodash';
import useScrollCheck from 'hooks/useScrollCheck';

const QuillContainer = styled.div<{
  $isMobile: boolean;
  $isResizeViewPort: boolean;
  height?: number;
  editorHeight: number;
}>`
  position: relative;
  width: 100%;
  height: ${props => props.height}px;
  max-height: ${props => props.height}px;
  overflow-y: auto;

  .quill {
    height: auto;

    .ql-editor {
      height: ${props => props.editorHeight}px;
      max-height: ${props => props.editorHeight}px;
      padding: 12px 20px 40px;

      overflow-y: scroll;
    }

    .ql-toolbar {
      /* top: ${props => (props.$isMobile ? '80px' : '0px')}; */
      top: ${props =>
        props.$isResizeViewPort ? '0px' : props.$isMobile ? '80px' : '0px'};

      padding: ${props => (props.$isMobile ? '0 20px 10px' : '32px 20px')};
    }

    /* .ql-video {
      width: 100%;
      aspect-ratio: 2/1;
      max-width: 100%;
    } */
  }
`;

const ImageContainer = styled.div<{ $isMobile: boolean }>`
  display: flex;
  align-items: center;
  position: absolute;
  right: ${props => (props.$isMobile ? '20px' : '20px')};
  top: ${props => (props.$isMobile ? '1px' : '20px')};
  padding: 2px 0;
  z-index: 2;

  input {
    display: none;
  }
`;

const GoTopButtonBox = styled.div<{
  $isMobile: boolean;
  $keyboardHeight: number;
}>`
  position: absolute;
  right: ${props => (props.$isMobile ? '20px' : '40px')};
  bottom: ${props =>
    props.$isMobile ? `${101 + props.$keyboardHeight}px` : '80px'};
  z-index: 5000;
`;

interface IntroduceModifyFormIProps {
  isMobile: boolean;
  editorValue: string;
  productInfo: Product;
  modifyValue: ModifyValue;
  height: number;
  setModifyValue: Dispatch<SetStateAction<ModifyValue>>;
  handleOnChangeProductParamInput: (key: string, value: any) => void;
}

const IntroduceModifyForm: React.FC<IntroduceModifyFormIProps> = props => {
  const {
    isMobile,
    editorValue,
    productInfo,
    modifyValue,
    height,
    setModifyValue,
    handleOnChangeProductParamInput,
  } = props;

  const {
    containerRef,
    quillRef,
    fileInputRef,
    value,
    isAbled,
    imageLoading,
    setValue,
    handleImageUploadClick,
    handleFileChange,
    handleResetIntroduce,
    handleImageDrop,
  } = useIntroduceModifyHooks(editorValue);

  const modalEmptyHeight = 64;
  const bottomSheetHeight = 90;

  const [isResizeViewPort, setIsResizeViewPort] = useState<boolean>(false);
  const [modalHeight, setModalHeight] = useState<number>(
    height - modalEmptyHeight,
  );
  const [editorHeight, setEditorHeight] = useState<number>(0);
  const [keyboardHeight, setKeyboardHeight] = useState<number>(0);

  const handleResizeViewPort = (keyboardHeight: { keyboardHeight: string }) => {
    setIsResizeViewPort(true);

    const quill = quillRef.current?.getEditor();
    const toolbar = quill?.getModule('toolbar').container;
    const toolbarHeight = toolbar.getBoundingClientRect().height;

    setKeyboardHeight(Number(keyboardHeight.keyboardHeight));
    setModalHeight(height - Number(keyboardHeight.keyboardHeight));
    setEditorHeight(
      height -
        bottomSheetHeight -
        toolbarHeight -
        Number(keyboardHeight.keyboardHeight),
    );
  };

  const handleResizeOnBlur = () => {
    setIsResizeViewPort(false);

    const quill = quillRef.current?.getEditor();
    const toolbar = quill?.getModule('toolbar').container;

    if (toolbar) {
      const toolbarHeight = toolbar?.getBoundingClientRect()?.height;

      setModalHeight(height - modalEmptyHeight);
      setEditorHeight(height - 233 - toolbarHeight);
    }
  };

  const handleRegistIntroduce = () => {
    handleOnChangeProductParamInput('htmlDetailContent', value);
  };

  useEffect(() => {
    HackleManager.logProductModifyProductDescriptionView(
      Number(productInfo.id),
      productInfo.title,
    );

    if (editorValue && quillRef.current) {
      const quill = quillRef.current?.getEditor();
      const toolbar = quill?.getModule('toolbar').container;
      const toolbarHeight = toolbar.getBoundingClientRect().height;
      setEditorHeight(height - 233 - toolbarHeight);
    }

    document.addEventListener(
      'click',
      handleTransformYouTubeVideoUrlByClick,
      true,
    );
    document.addEventListener(
      'keydown',
      handleTransformYouTubeVideoUrlByEnter,
      true,
    );

    return () => {
      document.removeEventListener(
        'click',
        handleTransformYouTubeVideoUrlByClick,
        true,
      );
      document.removeEventListener(
        'keydown',
        handleTransformYouTubeVideoUrlByEnter,
        true,
      );
    };
  }, []);

  const imageHandler = (imageDataUrl, type, imageData) => {
    const file = imageData.toFile();

    handleImageDrop(file);
  };

  const modules = useMemo(() => {
    return {
      toolbar: {
        container: toolbarContainer,
        handlers: {
          image: () => {
            handleImageUploadClick();
          },
          divider: function () {
            // 현재 커서 위치 가져오기
            const range = this.quill.getSelection(true);
            // 구분선 삽입
            this.quill.insertEmbed(range.index, 'divider', true);
            // 커서 위치 업데이트
            this.quill.setSelection(range.index + 2, 0);
          },
        },
      },
      table: false,
      'table-better': {
        menus: [
          'column',
          'row',
          'merge',
          'table',
          'cell',
          'wrap',
          'copy',
          'delete',
        ],
        toolbarTable: true,
      },
      keyboard: {
        bindings: QuillTableBetter.keyboardBindings,
      },
      imageDropAndPaste: {
        handler: imageHandler,
      },
    };
  }, []);

  const handleGotoTop = (behavior: 'auto' | 'smooth') => {
    const quillElement = document.querySelector('.ql-editor');
    if (quillElement) {
      quillElement.scrollTo({ top: 0, behavior });
    }
  };

  useEffect(() => {
    window.handleResizeOnBlur = handleResizeOnBlur;
    window.handleResizeViewPort = handleResizeViewPort;
  }, []);

  /* 에디터 스크롤 감지 */
  const { scroll } = useScrollCheck({
    targetElement: document.querySelector('.ql-editor'),
  });

  useEffect(() => {
    if (quillRef.current) {
      initEditorValue(quillRef, editorValue);

      const editor = quillRef.current.getEditor();

      // 이미지 근처 Enter 시 스크롤 점프 방지 (Quill 내부 스크롤 오버라이드)
      editor.scrollSelectionIntoView = () => {};

      editor.blur();
      handleGotoTop('auto');
    }
  }, [quillRef]);

  const renderEditorContainer = (): JSX.Element => {
    return (
      <QuillContainer
        ref={containerRef}
        $isMobile={isMobile}
        $isResizeViewPort={isResizeViewPort}
        editorHeight={editorHeight}
        height={height - 153}
        translate="no"
        className="notranslate"
      >
        {isMobile ? (
          !isResizeViewPort && (
            <ModalHeader
              title="소개글 수정하기"
              modifyValue={modifyValue}
              setModifyValue={setModifyValue}
              style={{
                padding: '24px 20px',
                borderRadius: '16px 16px 0 0',
              }}
            />
          )
        ) : (
          <TitleContainer
            title="소개글 수정하기"
            isMobile={isMobile}
            style={{
              position: 'relative',
              height: 'auto',
              padding: '32px 40px 0 ',
            }}
          />
        )}

        <ReactQuill
          theme="snow"
          modules={modules}
          // formats={formats}
          ref={quillRef}
          onChange={(value: string) => {
            // iframe 스타일 즉시 적용
            const applyIframeStyles = (html: string): string => {
              const parser = new DOMParser();
              const doc = parser.parseFromString(html, 'text/html');
              const iframes = doc.querySelectorAll('iframe');

              iframes.forEach(iframe => {
                iframe.style.width = '100%';
                iframe.style.aspectRatio = '2 / 1';
                iframe.style.maxWidth = '100%';
              });

              return doc.body.innerHTML;
            };

            const styledValue = applyIframeStyles(value);
            // 숨겨진 표 메타데이터 줄(<temporary>)에 갇힌 내용을 문단으로 꺼낸다
            setValue(normalizeTableTemporary(styledValue));
          }}
          onBlur={handleResizeOnBlur}
          onKeyDown={e => {
            // 붙여넣기 감지 (Ctrl+V 또는 Cmd+V)
            if (e.key === 'v' && (e.ctrlKey || e.metaKey)) {
              // 현재 스크롤 위치 저장
              const currentScrollTop = containerRef.current?.scrollTop || 0;

              // 스크롤 이동 방지
              const preventScroll = () => {
                if (containerRef.current) {
                  containerRef.current.scrollTop = currentScrollTop;
                }
              };

              // 스크롤 이벤트 리스너 추가
              containerRef.current?.addEventListener('scroll', preventScroll);

              setTimeout(() => {
                // 스크롤 이벤트 리스너 제거
                containerRef.current?.removeEventListener(
                  'scroll',
                  preventScroll,
                );

                const selection = quillRef.current?.getEditor().getSelection();
                if (selection && containerRef.current && quillRef.current) {
                  const editor = quillRef.current.getEditor();
                  const bounds = editor.getBounds(selection.index);
                  const containerHeight = containerRef.current.clientHeight;

                  // 한번에 최종 위치로 스크롤
                  if (
                    bounds.top + bounds.height >
                      currentScrollTop + containerHeight ||
                    bounds.top < currentScrollTop
                  ) {
                    containerRef.current.style.scrollBehavior = 'auto';
                    containerRef.current.scrollTop =
                      bounds.top - containerHeight / 2 + bounds.height;
                    // 부드러운 스크롤 복원
                    setTimeout(() => {
                      if (containerRef.current) {
                        containerRef.current.style.scrollBehavior = 'smooth';
                      }
                    }, 100);
                  }
                }
              }, 50);
            }
          }}
        />
        <ImageContainer $isMobile={isMobile}>
          <input
            ref={fileInputRef}
            type="file"
            multiple={false}
            onChange={handleFileChange}
            accept=".jpg, .png, .jpeg, .gif"
          />
        </ImageContainer>
      </QuillContainer>
    );
  };

  const renderContents = (): JSX.Element => {
    if (isMobile) {
      return (
        <ModalContainer
          isMobile={isMobile}
          isAbled={productInfo.status !== ProductState.CLOSED && isAbled}
          onClose={() => {
            setModifyValue({ ...modifyValue, modifyStatus: '' });
          }}
          floatingContainerMarginTop={0}
          onCloseText=""
          onAcceptText="적용"
          onClick={() => {
            if (isAbled) {
              callWebviewHandler(WebviewOperatorType.EditorResizeHandler, {
                isVisible: false,
              });

              HackleManager.logProductModifyItemSaveComplete(
                Number(productInfo.id),
                productInfo.title,
                '상세정보',
              );

              handleRegistIntroduce();

              setModifyValue({
                ...modifyValue,
                isModify: true,
                modifyStatus: '',
              });
            }
          }}
          modalStyle={{
            justifyContent: isResizeViewPort ? 'flex-start' : 'flex-end',
            padding: isResizeViewPort ? '0px' : '64px 0px 0px',
          }}
          style={{
            gap: '0px',
            height: `${modalHeight}px`,
            maxHeight: `${modalHeight}px`,
            borderRadius: `${isResizeViewPort ? '0px' : '16px 16px 0 0'}`,
            overflow: 'hidden',
          }}
        >
          {renderEditorContainer()}
        </ModalContainer>
      );
    }

    return (
      <Column style={{ position: 'relative' }}>
        {renderEditorContainer()}
        {productInfo.status !== ProductState.CLOSED && (
          <Row
            style={{
              position: 'sticky',
              bottom: 0,
              padding: '16px 40px',
              width: '100%',
              pointerEvents: 'none',
            }}
          >
            <SaveButton
              isAbled={isAbled}
              modifyValue={modifyValue}
              setModifyValue={setModifyValue}
              handleResetItem={handleResetIntroduce}
              handleRegistItem={() => {
                HackleManager.logProductModifyItemSaveComplete(
                  Number(productInfo.id),
                  productInfo.title,
                  '상세정보',
                );
                handleRegistIntroduce();
              }}
            />
          </Row>
        )}
      </Column>
    );
  };

  return (
    <EditorFormat>
      {imageLoading && <ImageUploadLoading />}
      {renderContents()}

      {scroll && (
        <GoTopButtonBox
          $isMobile={isMobile}
          $keyboardHeight={isResizeViewPort ? keyboardHeight : 0}
        >
          <GoTopButton handleGotoTop={() => handleGotoTop('smooth')} />
        </GoTopButtonBox>
      )}
    </EditorFormat>
  );
};

export default IntroduceModifyForm;

const headerHeight = 80;
const bottomButtonBoxHeight = 89;
const toolbarHeight = 67;
